/**
 * Proteção das rotas restritas a administradores.
 *
 * Papel: este arquivo exporta a `RotaDeAdministrador`, uma rota de layout que
 * consulta o acesso da pessoa logada e só renderiza o `Outlet` quando o papel
 * de administrador estiver presente.
 *
 * Como usar: no `router.tsx`, envolva as rotas restritas numa rota sem `path`
 * que monte este componente, dentro dos `children` da rota `/`:
 *
 * ```tsx
 * {
 *   Component: RotaDeAdministrador,
 *   children: [{ path: 'nome-da-rota', Component: ComponenteDaRota }],
 * }
 * ```
 *
 * A rota sem `path` não acrescenta segmento ao endereço: ela existe só para
 * interpor a verificação. Uma tela restrita declarada fora dessa rota continua
 * acessível a qualquer pessoa autenticada, e nada acusa o descuido — o endereço
 * abre normalmente.
 *
 * Esta é a barreira de conveniência, e não a de segurança. O que ela decide é
 * apenas o que a interface mostra; quem de fato impede o acesso ao dado é a
 * Function, chamando `exigirAdministrador` antes de tocar o banco. Uma tela
 * escondida aqui sem a rota correspondente protegida lá continua exposta a quem
 * chamar a API direto.
 */
import { Outlet } from 'react-router';

import { ErroDaApi } from '@/shared/api';
import { useAcesso } from '@/shared/hooks';

/**
 * Aviso exibido enquanto a sessão é consultada.
 *
 * O texto é estático pelo mesmo motivo da tela de espera do `LayoutProtegido`:
 * na maior parte das vezes ele dura poucos milissegundos, e um indicador
 * animado que aparece e some dentro do mesmo quadro incomoda mais do que a
 * ausência dele.
 *
 * @returns Aviso discreto, centralizado na área de conteúdo.
 */
function VerificandoPermissao() {
  return (
    <div className="py-16 text-center">
      <p className="text-sm text-muted-foreground">Verificando permissão…</p>
    </div>
  );
}

/**
 * Tela exibida quando a pessoa está autenticada mas não é administradora.
 *
 * O texto distingue os dois motivos possíveis, porque a ação de quem lê é
 * diferente em cada caso: faltar permissão se resolve pedindo o papel a quem
 * administra, e não conseguir consultar a sessão se resolve tentando de novo.
 *
 * @param motivo - `sem-permissao` quando a sessão respondeu e o papel não está
 *   nela; `indisponivel` quando a consulta não chegou a responder.
 * @returns Bloco centralizado com a explicação correspondente.
 */
function AcessoNegado({ motivo }: { motivo: 'sem-permissao' | 'indisponivel' }) {
  return (
    <div className="py-16 text-center">
      <h1 className="font-serif text-2xl text-foreground">
        {motivo === 'sem-permissao' ? 'Acesso restrito' : 'Não foi possível verificar'}
      </h1>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        {motivo === 'sem-permissao'
          ? 'Esta tela é restrita a administradores. Se você precisa dela, peça o papel a quem administra o sistema.'
          : 'Seu acesso não pôde ser consultado, então não dá para saber se você pode abrir esta tela.'}
      </p>
    </div>
  );
}

/**
 * Rota de layout que exige o papel de administrador.
 *
 * Renderiza o `Outlet` apenas quando o acesso trouxer `eAdministrador`. Erro na
 * consulta nega o acesso em vez de liberá-lo: uma API fora do ar não é razão
 * para mostrar tela restrita, e o caminho contrário transformaria indisponibilidade
 * em brecha.
 *
 * A consulta é a mesma do `useAcesso` da tela inicial, e por isso vem do cache
 * quando a pessoa chega por um cartão: a guarda só espera a rede quando o
 * endereço é aberto direto.
 *
 * @returns As rotas filhas, ou a tela de espera ou de acesso negado.
 */
export function RotaDeAdministrador() {
  const { data: acesso, error: erro, isPending } = useAcesso();

  if (isPending) return <VerificandoPermissao />;

  if (erro) {
    // 403 na consulta do acesso não deveria acontecer — a rota é aberta a
    // qualquer autenticado —, mas se acontecer é falta de permissão, não
    // indisponibilidade.
    const semPermissao = erro instanceof ErroDaApi && erro.status === 403;
    return <AcessoNegado motivo={semPermissao ? 'sem-permissao' : 'indisponivel'} />;
  }

  if (!acesso.eAdministrador) return <AcessoNegado motivo="sem-permissao" />;

  return <Outlet />;
}
